import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { timeAgo } from "@/components/NeedsContactCard";
import { CheckCircle2, Clock, Home } from "lucide-react";

export const STAGE_LABEL = { new: "New", contacted: "Contacted", qualified: "Qualified", readout_sent: "Readout sent", won: "Won", lost: "Lost" };
export const STAGE_COLOR = { new: "bg-orange-100 text-orange-900", contacted: "bg-sky-100 text-sky-900", qualified: "bg-indigo-100 text-indigo-900", readout_sent: "bg-violet-100 text-violet-900", won: "bg-emerald-100 text-emerald-900", lost: "bg-slate-200 text-slate-700" };

const VerifyChip = ({ s }) => (
  <span className={`text-[10px] uppercase tracking-wider font-semibold px-1.5 py-0.5 rounded inline-flex items-center gap-1 ${s === "verified" ? "bg-emerald-100 text-emerald-800" : "bg-amber-100 text-amber-800"}`}>
    {s === "verified" ? <CheckCircle2 className="w-3 h-3" /> : <Clock className="w-3 h-3" />}{s === "verified" ? "verified" : "unverified"}
  </span>
);

export default function Leads() {
  const [data, setData] = useState(null);
  const [stage, setStage] = useState("all");
  const [verify, setVerify] = useState("all");
  useEffect(() => { api.get("/admin/owner-leads/pipeline").then(r => setData(r.data)); }, []);

  const rows = useMemo(() => (data?.leads || []).filter(l =>
    (stage === "all" || (l.stage || "new") === stage) &&
    (verify === "all" || (verify === "verified" ? l.verification_status === "verified" : l.verification_status !== "verified"))
  ), [data, stage, verify]);

  if (!data) return <div className="text-sm text-slate-500">Loading…</div>;
  return (
    <div className="space-y-5" data-testid="leads-page">
      <div>
        <div className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold">Owner leads</div>
        <h1 className="text-3xl font-bold text-slate-900">Lead pipeline</h1>
        <p className="text-sm text-slate-500 mt-1">A lead is "uncontacted" until it leaves New. Reply only to verified emails.</p>
      </div>

      <div className="grid grid-cols-3 md:grid-cols-6 gap-2" data-testid="pipeline-stage-counts">
        {data.stages.map(s => (
          <button key={s} onClick={() => setStage(stage === s ? "all" : s)} data-testid={`pipeline-stage-${s}`}
            className={`text-left p-3 rounded-lg border transition-colors ${stage === s ? "border-slate-900 bg-slate-900 text-white" : "border-slate-200 bg-white hover:border-slate-400"}`}>
            <div className={`text-[10px] uppercase tracking-widest font-semibold ${stage === s ? "text-slate-300" : "text-slate-500"}`}>{STAGE_LABEL[s]}</div>
            <div className="text-2xl font-bold font-mono">{data.counts[s] || 0}</div>
          </button>
        ))}
      </div>

      <div className="flex flex-wrap gap-2 items-center" data-testid="pipeline-verify-filter">
        {[["all", "All"], ["verified", "Verified"], ["pending", "Unverified"]].map(([k, l]) => (
          <button key={k} onClick={() => setVerify(k)} data-testid={`pipeline-verify-${k}`}
            className={`px-3 py-1.5 text-xs rounded-full border min-h-[36px] ${verify === k ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-700 border-slate-200"}`}>{l}</button>
        ))}
        <span className="text-xs text-slate-500 ml-auto">{rows.length} lead{rows.length === 1 ? "" : "s"}</span>
      </div>

      <Card className="p-0 border-slate-200 overflow-hidden">
        {rows.length === 0 ? <div className="p-6 text-sm text-slate-500 italic">No leads in this view.</div> : (
          <table className="w-full text-sm">
            <thead className="bg-slate-900 text-white text-xs uppercase tracking-wider">
              <tr><th className="text-left px-3 py-2">Owner</th><th className="text-left px-3 py-2">Property</th><th className="text-left px-3 py-2">Stage</th><th className="text-left px-3 py-2">Email</th><th className="text-right px-3 py-2">Submitted</th></tr>
            </thead>
            <tbody>
              {rows.map(l => (
                <tr key={l.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`lead-row-${l.id}`}>
                  <td className="px-3 py-2"><Link to={`/leads/${l.id}`} className="font-semibold text-slate-900 underline-offset-2 hover:underline" data-testid={`lead-link-${l.id}`}>{l.name}</Link><div className="text-xs text-slate-500">{l.email}</div></td>
                  <td className="px-3 py-2 text-xs"><div className="flex items-center gap-1"><Home className="w-3 h-3 text-slate-400" />{(l.property || {}).address || "—"}</div>{l.property_id && <Link to={`/properties/${l.property_id}`} className="text-[11px] text-slate-500 underline">property record</Link>}</td>
                  <td className="px-3 py-2"><span className={`text-[10px] uppercase tracking-wider font-semibold px-2 py-0.5 rounded ${STAGE_COLOR[l.stage || "new"]}`}>{STAGE_LABEL[l.stage || "new"]}</span></td>
                  <td className="px-3 py-2"><VerifyChip s={l.verification_status} /></td>
                  <td className="px-3 py-2 text-right text-xs font-mono text-slate-500">{timeAgo(l.created_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>
    </div>
  );
}
